import * as React from 'react'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'

import contractVersionsPayrollPolicies from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import type { SupplementMatchDataType } from 'modules/payroll_bundle/components/common/helper/worksheet'
import useEmployeesByPeriod from 'modules/payroll_bundle/hooks/useEmployeesByPeriod'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { usePolicies } from 'modules/payroll_bundle/store/collections/policies'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import Matcher from './Matcher'

interface Props {
  matchedData: SupplementMatchDataType[]
}

interface MatcherProps extends Props {
  visibleEmployees: Employee[]
}

const MatcherByPeriod = ({ visibleEmployees, matchedData }: MatcherProps) => {
  const { currentPolicyPeriods: policyPeriods, periodIndex } = usePolicyPeriods()

  const policies = usePolicies()

  const { loading: loadingPolicyData } = useAsyncData(() => {
    return Promise.all([policies.fetch(), contractVersionsPayrollPolicies.fetch()])
  }, [])

  const filteredEmployees = useEmployeesByPeriod({
    policies,
    periodIndex,
    policyPeriods,
    loadingPolicyData,
    employees: visibleEmployees,
  })

  return <Matcher filteredEmployees={filteredEmployees} matchedData={matchedData} />
}

const EmployeeMatcher = ({ matchedData }: Props) => {
  const visibleEmployees = employees.filter(
    (employee) =>
      policyAuthorizations.for('payroll_supplements.supplements.read', employee) ||
      policyAuthorizations.for('payroll_supplements.observations.read', employee)
  )

  return <MatcherByPeriod visibleEmployees={visibleEmployees} matchedData={matchedData} />
}

export default observer(EmployeeMatcher)
